import React, { useEffect, useRef } from 'react'
import styled, { CSSObject } from 'styled-components'
import Paper, { PaperProps } from '@mui/material/Paper'
import MenuItem from '@mui/material/MenuItem'
import Box from '@mui/material/Box'
import Popper, { PopperProps } from '@mui/material/Popper'
import Tooltip from '@mui/material/Tooltip'
import TextField, { TextFieldProps } from '@mui/material/TextField'

import { DEFAULT_CHAR_LIMIT } from 'lib/GlobalConstants'
import { truncatedOptionLabel } from 'lib/Helper'
import ChipsList from 'components/Shared/ChipsList'
import { Tag } from 'containers/TextAnalysis/Types'
import { ELLIPSIS_LENGTH } from 'containers/TextAnalysis/Constants'

interface WrapperProps extends PaperProps {
  height?: number
}

export type MouseEvent =
  | React.MouseEvent<HTMLButtonElement>
  | React.SyntheticEvent
export interface OptionProps {
  [key: string]: any
}

export interface FilterOptionsState<T> {
  inputValue: string
  getOptionLabel: (option: T) => string
}

export const PaperWrapper = styled(Paper)<WrapperProps>(() => ({
  padding: '0.75rem',
  width: '22rem',

  '#tags-autocomplete-listbox': {
    maxHeight: '14.25rem',
  },
}))

export const PopperWrapper = styled(Popper)(
  () =>
    ({
      width: '100% !important',
      position: 'relative !important',
      transform: 'none !important',
    } as unknown as CSSObject),
)

export const ListItem = styled(MenuItem)({
  height: '2.625rem',
})

export const ListItemContent = styled(Box)({
  width: '100%',
})

export const renderPopperComponent = (props: PopperProps) => (
  <PopperWrapper {...props} placement="bottom-start" />
)

const StyledTagMenuTextField = styled(TextField)`
  .MuiInput-root {
    padding-bottom: 0.344rem !important;
  }
`

export const renderTagsInput = (
  props: TextFieldProps,
  label: string,
  placeholder: string,
) => {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, 0)
  }, [])

  const handleKeyDown = (event: any) => {
    const { key, target } = event

    if (target.value === '' && key === 'Delete') {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  return (
    <StyledTagMenuTextField
      {...props}
      variant="standard"
      label={label}
      inputRef={inputRef}
      placeholder={placeholder}
      onKeyDown={handleKeyDown}
    />
  )
}

export const renderCreateTagOption = (props: any, option: OptionProps) => {
  const { key, ...rest } = props
  return (
    <ListItem key={key} {...rest}>
      Create&nbsp;<strong>{option.title}</strong>
    </ListItem>
  )
}

export const renderListItemContent = (text: string) => (
  <ListItemContent>
    <Box>
      {text.length > DEFAULT_CHAR_LIMIT ? (
        <Tooltip placement="bottom" title={text}>
          <span>
            {truncatedOptionLabel(text, DEFAULT_CHAR_LIMIT + ELLIPSIS_LENGTH)}
          </span>
        </Tooltip>
      ) : (
        text
      )}
    </Box>
  </ListItemContent>
)

export const renderChipsList = (
  value: OptionProps,
  onChipDelete: (chip: string | Tag) => void,
  label: string,
) => {
  return value.map((option: OptionProps, index: number) => {
    return (
      <ChipsList
        variant="filled"
        size="small"
        key={index}
        chipDelete={true}
        chipData={[option]}
        chipLabel={label}
        onChipDelete={onChipDelete}
      />
    )
  })
}
